/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import {
	Modal,
	Button,
	Group,
	Text,
	Box,
	ActionIcon,
	Alert,
} from '@mantine/core';

// Icons
import {
	IconX,
	IconTrash,
	IconInfoCircle,
	IconDownload,
	IconCheck,
} from '@tabler/icons-react';

// Utils
import {
	addZeroToTenPlace,
	truncateString,
} from '@utils/functions';

// Styles
import classes from './MultipleSelectionModal.module.css';
import clsx from 'clsx';

import { Tooltip } from '@components/atoms/Tooltip';

// I18n
import { useTranslation } from 'react-i18next';
import isNil from 'lodash/isNil';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const MultipleSelectionModal = (props: {
	onConfirm: () => void;
	onClose: () => void;
	onRemove: (data: any) => void;
	opened: boolean;
	data: any;
	type: string;
	model: string;
}) => {
	/* ******************************* Hooks ****************************** */
	const { t } = useTranslation();

	/* ******************************* Constants ****************************** */
	const NBR_TOTAL_ITEMS = props.data.length;
	const NBR_DATA_NOT_VERIFIED = props.data.filter(
		(item: any) => !item.verified
	).length;

	const NBR_DATA_VERIFIED =
		NBR_TOTAL_ITEMS - NBR_DATA_NOT_VERIFIED;

	/* ******************************** Helpers ******************************* */
	const isNotValidToConfirm = () => {
		switch (props.type) {
			case 'verify':
				if (NBR_DATA_NOT_VERIFIED == 0) return true;
				break;
			case 'delete':
				if (NBR_DATA_NOT_VERIFIED == 0) return true;
				break;
			default:
				return false;
		}
	};

	const getButtonLabel = (model: string) => {
		switch (model) {
			case 'production':
				return 'productions';
			case 'conversionFactor':
				return 'conversion factors';
			default:
				return '';
		}
	};

	const getConfirmButton = () => {
		switch (props.type) {
			case 'delete':
				return (
					<Button
						data-testid="W0FtGXRw_F5jdHi6B-button"
						className={classes.button}
						size="sm"
						color="error"
						onClick={() => {
							props.onConfirm();
						}}
						disabled={
							props.data.length == 0 ||
							isNotValidToConfirm()
						}
						leftSection={<IconTrash size={18} />}
					>
						{t('global.delete')}{' '}
						{getButtonLabel(props.model)}
					</Button>
				);

			case 'verify':
				return (
					<Button
						data-testid="W1FtGXRw_F5jdHi6B-button"
						leftSection={<IconCheck size={18} />}
						className={classes.button}
						size="sm"
						onClick={() => {
							props.onConfirm();
						}}
						disabled={
							props.data.length == 0 ||
							isNotValidToConfirm()
						}
					>
						{t('global.verify')}{' '}
						{getButtonLabel(props.model)}
					</Button>
				);
			case 'download':
				return (
					<Button
						data-testid="D1FtGXwl_F5jdHi6B-button"
						leftSection={<IconDownload size={18} />}
						className={classes.button}
						size="sm"
						onClick={() => {
							props.onConfirm();
						}}
						disabled={
							props.data.length == 0 ||
							isNotValidToConfirm()
						}
					>
						{t('global.download')}{' '}
						{getButtonLabel(props.model)}
					</Button>
				);

			default:
				break;
		}
	};

	const getAlert = () => {
		switch (props.type) {
			case 'delete':
				return (
					<Alert
						icon={<IconInfoCircle />}
						variant="light"
						color="error"
						title={t('productions.alert.delete.title')}
					>
						{NBR_DATA_VERIFIED}{' '}
						{t(
							'productions.alert.delete.itemsAlreadyVerified'
						)}
					</Alert>
				);

			case 'verify':
				return (
					<Alert
						icon={<IconInfoCircle />}
						variant="light"
						color="blue"
						title={t('productions.alert.verify.title')}
					>
						{NBR_DATA_VERIFIED}{' '}
						{t(
							'productions.alert.verify.itemsAlreadyVerified'
						)}
					</Alert>
				);
			case 'download':
				return null;

			default:
				break;
		}
	};

	return (
		<Modal
			opened={props.opened}
			onClose={props.onClose}
			classNames={classes}
			withCloseButton={false}
			size={500}
		>
			<Text className={classes.title}>
				{addZeroToTenPlace(NBR_TOTAL_ITEMS)}{' '}
				{t('global.selected')} production
			</Text>
			<Box className={classes.customBody}>
				<Box className={classes.header}>
					<Box className={classes.columnHeader}>
						{t('global.project')}
					</Box>
					<Box className={classes.columnHeader}>
						{t('global.startDate')}
					</Box>
					<Box className={classes.columnHeader}>
						{t('global.production')}
					</Box>
					<Box className={classes.columnCancel}></Box>
				</Box>
				{props.data.map((item: any) => (
					<Box
						key={item.id}
						className={clsx(
							classes.row,
							item.verified &&
								props.type != 'download' &&
								classes.rowInvalideVerify,
							props.model == 'conversionFactor' &&
								item.isUsed &&
								classes.rowInvalideVerify
						)}
					>
						<Tooltip
							label={!isNil(item.project) && item.project}
							isActive={
								!isNil(item.project) &&
								item.project.length > 17
							}
						>
							<Box className={classes.column}>
								{truncateString(item.project, 17)}
							</Box>
						</Tooltip>
						{props.model == 'production' && (
							<Tooltip
								label={
									!isNil(item.entity) &&
									!isNil(item.entity.name) &&
									item.entity.name
								}
								isActive={
									!isNil(item.entity) &&
									!isNil(item.entity.name) &&
									item.entity.name.length > 17
								}
							>
								<Box className={classes.column}>
									{truncateString(item.startDate, 17)}
								</Box>
							</Tooltip>
						)}
						{props.model == 'conversionFactor' && (
							<Tooltip
								label={!isNil(item.entity) && item.entity}
								isActive={
									!isNil(item.entity) &&
									item.entity.length > 17
								}
							>
								<Box className={classes.column}>
									{truncateString(item.entity, 17)}
								</Box>
							</Tooltip>
						)}
						<Tooltip
							label={!isNil(item.site) && item.site}
							isActive={
								!isNil(item.site) && item.site.length > 17
							}
						>
							<Box className={classes.column}>
								{truncateString(item.production, 17)}
							</Box>
						</Tooltip>
						<Box className={classes.columnCancel}>
							<ActionIcon
								data-testid={item.id}
								variant="transparent"
								onClick={() => {
									props.onRemove(item);
								}}
							>
								<IconX
									color="var(--mantine-color-grey)"
									size={'15px'}
								/>
							</ActionIcon>
						</Box>
					</Box>
				))}
			</Box>
			<Box
				style={{
					marginTop: '10px',
					marginRight: '15px',
				}}
			>
				{NBR_DATA_VERIFIED != 0 && getAlert()}
			</Box>
			<Box className={classes.containerButtons}>
				<Group mt="xl">
					<Button
						data-testid="D1CtGXwC_F5jdHi6B-button"
						size="sm"
						color="gray"
						variant="transparent"
						onClick={() => {
							props.onClose();
						}}
						className={classes.cancel}
					>
						{t('global.cancel')}
					</Button>
					{getConfirmButton()}
				</Group>
			</Box>
		</Modal>
	);
};
